import { defineStore } from "pinia";
import Vue from "vue";
import APP_LAYOUT_STATUS_QUERY from "@/graphql/appLayoutStatus.graphql";
import { useUserStore } from "~/stores/user";
import { requestInFlight, trackRequest } from "~/stores/user/inFlightRequests";

export const APP_LAYOUT_STATUS_KEYS = ["lastPayment", "validSubscriptions"];

const belongsTo = (data, me) =>
  !!data && !!me && String(data.id) === String(me.id);

/**
 * The payment and trial status of the logged-in person
 * (`graphql/appLayoutStatus.graphql`: `lastPayment`, `validSubscriptions`),
 * kept out of `me`. The app layout refreshes it on mount and in its payment
 * poll; the readers that act on it (payments, the Profile card, the type
 * toggle, the job feed's trial event, the pricing cards) call `load()`, which
 * waits for the layout's request instead of sending another.
 *
 * Data is returned only while it belongs to the logged-in person; until it is
 * loaded, nothing counts as a pending payment or a trial.
 */
export const useAppLayoutStatusStore = defineStore("appLayoutStatusStore", {
  state: () => ({
    loaded: null,
    pending: null,
  }),
  getters: {
    /**
     * The status of the logged-in person, or null until it is loaded.
     * @returns {object | null}
     */
    data(state) {
      return belongsTo(state.loaded, useUserStore().me) ? state.loaded : null;
    },
    isLoaded() {
      return !!this.data;
    },
    lastPayment() {
      return this.data?.lastPayment ?? null;
    },
    validSubscriptions() {
      return this.data?.validSubscriptions ?? [];
    },
    /**
     * The free trial among the valid subscriptions, or null.
     * @returns {object | null}
     */
    freeTrialSubscription() {
      return this.validSubscriptions.find((sub) => sub.isFree) ?? null;
    },
    hasFreeTrial() {
      return !!this.freeTrialSubscription;
    },
    /**
     * Whether a valid subscription includes the phone number, or null until
     * the status is loaded.
     * @returns {boolean | null}
     */
    isMobileIncluded() {
      return this.data
        ? this.validSubscriptions.some((sub) => sub.isMobileIncluded)
        : null;
    },
    /**
     * The promo code of the valid subscriptions, or an empty string.
     * @returns {string}
     */
    promoCode() {
      return (
        this.validSubscriptions.find((sub) => sub.promoCode)?.promoCode ?? ""
      );
    },
    pendingPayment() {
      return (
        this.lastPayment?.status === "started" &&
        !!this.lastPayment.isSubscription
      );
    },
    pendingCardReg() {
      return (
        this.lastPayment?.status === "started" &&
        !!this.lastPayment.isCardRegistration
      );
    },
    failedPayment() {
      return (
        this.lastPayment?.status === "error" &&
        !!this.lastPayment.isSubscription
      );
    },
    failedCardReg() {
      return (
        this.lastPayment?.status === "error" &&
        !!this.lastPayment.isCardRegistration
      );
    },
    paymentBlocked() {
      return this.pendingPayment || this.pendingCardReg;
    },
    paymentFailed() {
      return (
        this.pendingPayment ||
        this.failedPayment ||
        this.pendingCardReg ||
        this.failedCardReg
      );
    },
    paymentFailedReason() {
      if (this.failedPayment) {
        return "announcements.paymentFailed";
      } else if (this.failedCardReg) {
        return "announcements.cardRegFailed";
      } else if (this.pendingPayment) {
        return "announcements.paymentPending";
      } else if (this.pendingCardReg) {
        return "announcements.cardRegPending";
      }
      return undefined;
    },
  },
  actions: {
    /**
     * Loads the status once per person, without sending a second request
     * while one is in flight (the layout's mount request, a poll tick).
     * Requests in flight, also a refresh of loaded data or one that replaced
     * this call's own request, are waited for, so the call never returns with
     * data that is about to be replaced or without the data a newer request
     * brings.
     */
    async load() {
      if (!useUserStore().me) {
        return;
      }
      if (!this.data && !requestInFlight(this, "status", this.pending)) {
        await this.sendQuery();
      }
      let pending = requestInFlight(this, "status", this.pending);
      while (pending) {
        await pending.catch(() => {});
        pending = requestInFlight(this, "status", this.pending);
      }
    },

    /**
     * Reloads the status. A response that arrives after a newer request was
     * sent, or after `setValues`, is dropped. A failed request keeps the last
     * status.
     */
    async refresh() {
      if (!useUserStore().me) {
        return;
      }
      await this.sendQuery();
    },

    /**
     * Reloads the status, or waits for the request in flight if there is one
     * (the layout's mount request on a full load), for pages that show the
     * status fresh on each visit.
     */
    async refreshUnlessInFlight() {
      const pending = requestInFlight(this, "status", this.pending);
      if (pending) {
        await pending.catch(() => {});
        return;
      }
      await this.refresh();
    },

    /**
     * Applies the status fields of a result (a mutation's person, the payment
     * poll) to the loaded status.
     * @param {object} values
     */
    setValues(values) {
      const me = useUserStore().me;
      const data = this.data;
      if (!data || (values.id != null && String(values.id) !== String(me.id))) {
        return;
      }
      this.pending = null;
      for (const key of APP_LAYOUT_STATUS_KEYS) {
        if (key in values) {
          Vue.set(data, key, values[key]);
        }
      }
    },

    async sendQuery() {
      const userStore = useUserStore();
      const query = this.$nuxt.$query(
        APP_LAYOUT_STATUS_QUERY,
        {},
        { skipErrorPageOnAuthError: true }
      );
      const id = trackRequest(this, "status", query);
      this.pending = id;
      try {
        const result = await query;
        if (this.pending === id && belongsTo(result?.me, userStore.me)) {
          this.loaded = {
            id: result.me.id,
            lastPayment: result.me.lastPayment ?? null,
            validSubscriptions: result.me.validSubscriptions ?? [],
          };
        }
      } catch (error) {
        this.$nuxt.$logger.warn("Failed to load the app layout status", error);
      } finally {
        if (this.pending === id) {
          this.pending = null;
        }
      }
    },
  },
});
